'use client'

import {
    Client,
    getClient,
    getClientsByOrganization,
} from '@/lib/api/clients/client-api'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdClose, MdPerson, MdSearch } from 'react-icons/md'

interface Props {
    value: number | null
    onChange: (clientId: number | null, client: Client | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    size?: 'sm' | 'md'
}

export function ClientSelector({
    value,
    onChange,
    label = 'Cliente',
    placeholder = 'Selecione um cliente',
    disabled = false,
    className = '',
    size = 'md',
}: Props) {
    const { user } = useAuth()
    const [results, setResults] = useState<Client[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selectedClient, setSelectedClient] = useState<Client | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Carregar resultados iniciais ao abrir o dropdown
    const loadInitial = useCallback(async () => {
        if (!user?.companyId) return
        setLoading(true)
        try {
            const response = await getClientsByOrganization(
                user.organizationId,
                { page: 1, limit: 50 }
            )
            if (response.success) {
                setResults(
                    Array.isArray(response.result) ? response.result : []
                )
            }
        } catch {
            // silencioso
        } finally {
            setLoading(false)
        }
    }, [user?.companyId])

    // Buscar por termo com debounce
    const searchByTerm = useCallback(
        async (term: string) => {
            if (!user?.companyId) return
            setLoading(true)
            try {
                const response = await getClientsByOrganization(
                    user.organizationId,
                    {
                        page: 1,
                        limit: 50,
                        search: term || undefined,
                    }
                )
                if (response.success) {
                    setResults(
                        Array.isArray(response.result) ? response.result : []
                    )
                }
            } catch {
                // silencioso
            } finally {
                setLoading(false)
            }
        },
        [user?.companyId]
    )

    // Buscar cliente selecionado pelo value (prop) se necessário
    useEffect(() => {
        if (value && !selectedClient) {
            if (!user?.companyId) return
            getClient(value).then(response => {
                if (response.success && response.result) {
                    setSelectedClient(response.result)
                }
            })
        } else if (!value) {
            setSelectedClient(null)
        }
    }, [value, selectedClient, user?.companyId])

    // Debounce na digitação
    useEffect(() => {
        if (!showDropdown) return

        if (debounceRef.current) clearTimeout(debounceRef.current)

        debounceRef.current = setTimeout(() => {
            searchByTerm(searchTerm)
        }, 300)

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [searchTerm, showDropdown, searchByTerm])

    // Click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleFocus = () => {
        setShowDropdown(true)
        if (results.length === 0) {
            loadInitial()
        }
    }

    const handleSelect = (client: Client) => {
        setSelectedClient(client)
        onChange(client.id, client)
        setShowDropdown(false)
        setSearchTerm('')
    }

    const handleClear = () => {
        setSelectedClient(null)
        onChange(null, null)
        setSearchTerm('')
    }

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            {label && (
                <label className="label">
                    <span className="label-text">{label}</span>
                </label>
            )}

            <div className="relative">
                {selectedClient ? (
                    <div
                        className={
                            'flex items-center gap-2 input input-bordered w-full pr-10 ' +
                            (size === 'sm' ? 'input-sm!' : '')
                        }
                    >
                        <MdPerson className="text-lg text-secondary flex-shrink-0" />
                        <span className="truncate">{selectedClient.name}</span>
                        {!disabled && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="text"
                            placeholder={placeholder}
                            className={
                                'input input-bordered w-full pl-10 ' +
                                (size === 'sm' ? 'input-sm!' : '')
                            }
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value)
                                setShowDropdown(true)
                            }}
                            onFocus={handleFocus}
                            disabled={disabled}
                            autoComplete="off"
                            aria-autocomplete="none"
                        />
                    </>
                )}

                {showDropdown && !selectedClient && (
                    <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {loading ? (
                            <div className="p-4 text-center">
                                <span className="loading loading-spinner loading-sm"></span>
                            </div>
                        ) : results.length === 0 ? (
                            <div className="p-4 text-center text-base-content/60 text-sm">
                                {searchTerm
                                    ? 'Nenhum cliente encontrado'
                                    : 'Nenhum cliente cadastrado'}
                            </div>
                        ) : (
                            results.map(client => (
                                <button
                                    key={client.id}
                                    type="button"
                                    className="w-full px-4 py-3 text-left hover:bg-base-200 flex items-center gap-3 border-b border-base-200 last:border-b-0"
                                    onClick={() => handleSelect(client)}
                                >
                                    <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                        <MdPerson className="text-secondary" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium truncate">
                                            {client.name}
                                        </p>
                                        <p className="text-xs text-base-content/60 truncate">
                                            {client.email ||
                                                client.whatsAppNumber ||
                                                'Sem contato'}
                                        </p>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
